'use client'

import { memo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Check, X, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Banks section rail divider with traveling pulse */
export const NinxlBanksRailDivider = memo(function NinxlBanksRailDivider({
  label,
}: {
  label: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className="ninxl-banks-rail-divider mx-auto flex max-w-5xl items-center gap-3 px-6 py-5" aria-hidden>
      <div className="ninxl-banks-rail-line relative flex-1 overflow-hidden">
        {!shouldReduce ? <span className="ninxl-banks-rail-pulse ninxl-banks-rail-pulse--left" /> : null}
      </div>
      <motion.span
        className="ninxl-banks-rail-label ninxl-8bit-label"
        initial={shouldReduce ? false : { opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.35 }}
      >
        {label}
      </motion.span>
      <div className="ninxl-banks-rail-line relative flex-1 overflow-hidden">
        {!shouldReduce ? <span className="ninxl-banks-rail-pulse ninxl-banks-rail-pulse--right" /> : null}
      </div>
    </div>
  )
})

/** 2×2 risk matrix */
export const NinxlBanksRiskMatrix = memo(function NinxlBanksRiskMatrix({
  risks,
  className,
}: {
  risks: readonly { code: string; title: string; desc: string; severity: 'high' | 'medium'; icon: LucideIcon }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className={cn('grid gap-3 sm:grid-cols-2', className)}>
      {risks.map((risk, index) => (
        <motion.div
          key={risk.code}
          className={cn(
            'ninxl-banks-risk-cell ninxl-pixel-border p-4 md:p-5',
            risk.severity === 'high' ? 'ninxl-banks-risk-cell--high' : 'ninxl-banks-risk-cell--medium',
            risk.severity === 'high' && !shouldReduce && 'ninxl-banks-risk-cell--pulse',
          )}
          initial={shouldReduce ? false : { opacity: 0, y: 20, rotate: index % 2 === 0 ? -1 : 1 }}
          whileInView={{ opacity: 1, y: 0, rotate: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5, delay: index * 0.09, ease: [0.22, 1, 0.36, 1] }}
          whileHover={shouldReduce ? undefined : { y: -4, transition: { duration: 0.2 } }}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="ninxl-banks-risk-code">{risk.code}</span>
            <span
              className={cn(
                'ninxl-banks-severity-pill',
                risk.severity === 'high' ? 'ninxl-banks-severity-pill--high ninxl-banks-severity-pill--blink' : 'ninxl-banks-severity-pill--medium',
              )}
            >
              {risk.severity === 'high' ? 'HIGH' : 'MED'}
            </span>
          </div>
          <div className="mb-2 flex items-center gap-2">
            <risk.icon className="ninxl-theme-text-accent h-4 w-4 shrink-0" aria-hidden />
            <h3 className="ninxl-theme-text text-sm font-bold md:text-base">{risk.title}</h3>
          </div>
          <p className="ninxl-theme-text-muted text-xs leading-relaxed md:text-sm">{risk.desc}</p>
        </motion.div>
      ))}
    </div>
  )
})

/** Horizontal transaction rail with traveling settlement dot */
export const NinxlBanksTransactionRail = memo(function NinxlBanksTransactionRail({
  nodes,
  className,
}: {
  nodes: readonly { label: string; detail: string }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className={cn('relative', className)}>
      <div className="ninxl-banks-rail-track-wrap relative mb-6 hidden md:block" aria-hidden>
        <div className="ninxl-banks-rail-track" />
        {!shouldReduce ? <span className="ninxl-banks-rail-traveler" /> : null}
      </div>
      <div className="grid gap-4 md:grid-cols-4">
        {nodes.map((node, index) => (
          <motion.div
            key={node.label}
            className="ninxl-banks-rail-node ninxl-pixel-border relative p-4 text-center"
            initial={shouldReduce ? false : { opacity: 0, y: 18, scale: 0.94 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-30px' }}
            transition={{ duration: 0.45, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={shouldReduce ? undefined : { scale: 1.03, transition: { duration: 0.2 } }}
          >
            <div className="ninxl-banks-rail-node-index ninxl-8bit-label mb-2">NODE {index + 1}</div>
            <div className="ninxl-banks-rail-node-light mx-auto mb-2" aria-hidden />
            <div className="ninxl-theme-text mb-1 text-sm font-bold">{node.label}</div>
            <p className="ninxl-theme-text-muted text-xs leading-relaxed">{node.detail}</p>
            {index < nodes.length - 1 ? (
              <span
                className={cn(
                  'ninxl-banks-rail-arrow absolute -right-3 top-1/2 hidden -translate-y-1/2 md:block',
                  !shouldReduce && 'ninxl-banks-rail-arrow--pulse',
                )}
                aria-hidden
              >
                →
              </span>
            ) : null}
          </motion.div>
        ))}
      </div>
    </div>
  )
})

/** Vertical settlement ledger */
export const NinxlBanksSettlementLedger = memo(function NinxlBanksSettlementLedger({
  entries,
  className,
}: {
  entries: readonly { phase: string; detail: string; side: 'debit' | 'credit' }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()
  const lastIndex = entries.length - 1

  return (
    <div className={cn('ninxl-banks-settlement-ledger ninxl-pixel-border divide-y-2 divide-[color:var(--ninxl-8bit-dim)]', className)}>
      <div className="ninxl-banks-ledger-header grid grid-cols-[80px_1fr_72px] gap-3 px-4 py-3 md:px-5">
        <span className="ninxl-8bit-label text-[9px]">SIDE</span>
        <span className="ninxl-8bit-label text-[9px]">PHASE</span>
        <span className="ninxl-8bit-label text-right text-[9px]">STATUS</span>
      </div>
      {entries.map((entry, index) => (
        <motion.div
          key={entry.phase}
          className={cn(
            'ninxl-banks-ledger-row grid grid-cols-[80px_1fr_72px] items-center gap-3 px-4 py-3 md:px-5',
            index === lastIndex && !shouldReduce && 'ninxl-banks-ledger-row--sealed',
          )}
          initial={shouldReduce ? false : { opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-20px' }}
          transition={{ duration: 0.45, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <span
            className={cn(
              'ninxl-banks-ledger-side text-[10px] font-bold',
              entry.side === 'debit' ? 'ninxl-banks-ledger-side--debit' : 'ninxl-banks-ledger-side--credit',
            )}
          >
            {entry.side.toUpperCase()}
          </span>
          <div>
            <div className="ninxl-theme-text text-sm font-semibold">{entry.phase}</div>
            <p className="ninxl-theme-text-muted text-xs">{entry.detail}</p>
          </div>
          <span
            className={cn(
              'ninxl-banks-ledger-posted ninxl-8bit-label text-right text-[8px]',
              index === lastIndex && 'ninxl-banks-ledger-sealed',
            )}
          >
            {index < lastIndex ? 'POSTED' : 'SEALED'}
          </span>
        </motion.div>
      ))}
    </div>
  )
})

/** Regulatory fit matrix */
export const NinxlBanksRegulatoryMatrix = memo(function NinxlBanksRegulatoryMatrix({
  rows,
  className,
}: {
  rows: readonly { framework: string; requirement: string; cloud: boolean; legacy: boolean; ninx: boolean }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  const Mark = ({ pass, highlight, delay }: { pass: boolean; highlight?: boolean; delay: number }) => (
    <motion.span
      className="inline-flex"
      initial={shouldReduce ? false : { opacity: 0, scale: 0 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay, type: 'spring', stiffness: 420, damping: 16 }}
    >
      {pass ? (
        <Check className={cn('mx-auto h-3.5 w-3.5', highlight ? 'text-[#21728d]' : 'text-emerald-600')} aria-label="Met" />
      ) : (
        <X className="mx-auto h-3.5 w-3.5 text-rose-500/70" aria-label="Not met" />
      )}
    </motion.span>
  )

  return (
    <motion.div
      className={cn('ninxl-banks-reg-matrix ninxl-pixel-border overflow-x-auto', className)}
      id="banks-matrix"
      initial={shouldReduce ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5 }}
    >
      <table className="min-w-[640px] w-full border-collapse text-left">
        <thead>
          <tr className="ninxl-banks-reg-matrix-head border-b-2 border-[color:var(--ninxl-8bit-dim)]">
            <th className="ninxl-8bit-label px-4 py-3 text-[9px] md:px-5">FRAMEWORK</th>
            <th className="ninxl-8bit-label px-4 py-3 text-[9px] md:px-5">REQUIREMENT</th>
            <th className="ninxl-8bit-label px-2 py-3 text-center text-[9px]">CLOUD</th>
            <th className="ninxl-8bit-label px-2 py-3 text-center text-[9px]">LEGACY</th>
            <th className="ninxl-8bit-label ninxl-banks-reg-matrix-ninx px-2 py-3 text-center text-[9px]">NINX</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <motion.tr
              key={`${row.framework}-${row.requirement}`}
              className="border-b border-[color:var(--ninxl-8bit-dim)]/60"
              initial={shouldReduce ? false : { opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-20px' }}
              transition={{ duration: 0.4, delay: rowIndex * 0.07 }}
            >
              <td className="ninxl-banks-framework px-4 py-3 text-xs font-bold md:px-5">{row.framework}</td>
              <td className="ninxl-theme-text-muted px-4 py-3 text-xs md:px-5">{row.requirement}</td>
              <td className="px-2 py-3 text-center">
                <Mark pass={row.cloud} delay={rowIndex * 0.07 + 0.15} />
              </td>
              <td className="px-2 py-3 text-center">
                <Mark pass={row.legacy} delay={rowIndex * 0.07 + 0.22} />
              </td>
              <td className="ninxl-banks-reg-matrix-ninx px-2 py-3 text-center">
                <Mark pass={row.ninx} highlight delay={rowIndex * 0.07 + 0.3} />
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  )
})

/** Horizontal controls rail: auto-scroll marquee */
export const NinxlBanksControlsRail = memo(function NinxlBanksControlsRail({
  controls,
  className,
}: {
  controls: readonly { code: string; title: string; desc: string; icon: LucideIcon }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()
  const track = [...controls, ...controls]

  return (
    <div className={cn('ninxl-banks-controls-marquee -mx-2 overflow-hidden px-2 pb-2', className)}>
      <div className={cn('flex min-w-max gap-4', !shouldReduce && 'ninxl-banks-controls-track')}>
        {track.map((ctrl, index) => (
          <motion.div
            key={`${ctrl.code}-${index}`}
            className="ninxl-banks-control-card ninxl-pixel-border w-[220px] shrink-0 p-4 md:w-[240px]"
            whileHover={shouldReduce ? undefined : { y: -5, transition: { duration: 0.2 } }}
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="ninxl-banks-control-code">{ctrl.code}</span>
              <ctrl.icon className="ninxl-theme-text-accent h-4 w-4" aria-hidden />
            </div>
            <h3 className="ninxl-theme-text mb-1 text-sm font-bold">{ctrl.title}</h3>
            <p className="ninxl-theme-text-muted text-xs leading-relaxed">{ctrl.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  )
})

/** Institution bento grid */
export const NinxlBanksInstitutionBento = memo(function NinxlBanksInstitutionBento({
  institutions,
  className,
}: {
  institutions: readonly { code: string; title: string; desc: string; icon: LucideIcon; featured?: boolean }[]
  className?: string
}) {
  const shouldReduce = useReducedMotion()

  return (
    <div className={cn('grid gap-3 md:grid-cols-2 md:grid-rows-2', className)}>
      {institutions.map((inst, index) => (
        <motion.div
          key={inst.code}
          className={cn(
            'ninxl-banks-bento-tile ninxl-pixel-border p-5',
            inst.featured && 'md:row-span-2 md:flex md:flex-col md:justify-center ninxl-banks-bento-tile--featured',
          )}
          initial={shouldReduce ? false : { opacity: 0, scale: 0.92 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-30px' }}
          transition={{ duration: 0.5, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
          whileHover={shouldReduce ? undefined : { scale: 1.02, transition: { duration: 0.25 } }}
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <span className="ninxl-banks-institution-code">{inst.code}</span>
            <motion.div
              className="ninxl-banks-bento-icon flex h-9 w-9 items-center justify-center"
              animate={shouldReduce || !inst.featured ? undefined : { rotate: [0, -6, 6, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <inst.icon className="h-4 w-4" aria-hidden />
            </motion.div>
          </div>
          <h3 className="ninxl-theme-text-accent mb-2 text-sm font-bold md:text-base">{inst.title}</h3>
          <p className="ninxl-theme-text-muted text-xs leading-relaxed md:text-sm">{inst.desc}</p>
        </motion.div>
      ))}
    </div>
  )
})

export const NinxlBanksSectionBackdrop = memo(function NinxlBanksSectionBackdrop({
  variant,
  className,
}: {
  variant: 'risk' | 'rail' | 'ledger' | 'institutions'
  className?: string
}) {
  const washes: Record<typeof variant, string> = {
    risk: 'from-rose-500/[0.03] via-transparent to-amber-500/[0.02]',
    rail: 'from-[#21728d]/[0.05] via-transparent to-emerald-500/[0.03]',
    ledger: 'from-emerald-500/[0.03] via-transparent to-[#21728d]/[0.02]',
    institutions: 'from-[#134b5f]/[0.04] via-transparent to-transparent',
  }

  return (
    <div
      className={cn('pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br', washes[variant], className)}
      aria-hidden
    />
  )
})